import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { X, Loader2, User, School, Users as UsersIcon, GraduationCap } from 'lucide-react';
import { BackButton } from './detail-primitives';
import TeacherDetail from './TeacherDetail';
import StudentDetail from './StudentDetail';
import ParentDetail from './ParentDetail';
import ClassroomDetail from './ClassroomDetail';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const KIND_META = {
  teacher:   { icon: GraduationCap, label: 'بطاقة المعلم',     Body: TeacherDetail },
  student:   { icon: User,          label: 'بطاقة الطالب',     Body: StudentDetail },
  parent:    { icon: UsersIcon,     label: 'بطاقة ولي الأمر',  Body: ParentDetail },
  classroom: { icon: School,        label: 'بطاقة القاعة',     Body: ClassroomDetail },
};

/**
 * Generic detail modal that fetches /api/global-search/details/{kind}/{id}
 * and dispatches to the appropriate per-kind Body component.
 *
 * Modal stack is owned by the parent (GlobalSearch); this component is
 * stateless about navigation. It only renders the TOP entry plus a
 * back affordance if `canGoBack` is true.
 */
const EntityDetailModal = ({ entity, onOpenEntity, onBack, onClose, onNavigate, canGoBack }) => {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    if (!entity) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setData(null);
    axios.get(
      `${API_URL}/api/global-search/details/${entity.kind}/${entity.id}`,
      { withCredentials: true },
    )
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.detail || 'تعذّر تحميل التفاصيل.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [entity]);

  if (!entity) return null;
  const meta = KIND_META[entity.kind];
  if (!meta) return null;
  const Icon = meta.icon;
  const Body = meta.Body;

  return createPortal(
    <div
      className="fixed inset-0 z-[130] flex items-start justify-center pt-[8vh] px-4"
      dir="rtl"
      data-testid={`entity-modal-${entity.kind}`}
      role="dialog"
      aria-modal="true"
      onClick={(e) => e.stopPropagation()}
    >
      <div
        className="absolute inset-0 bg-[#0A1128]/70 backdrop-blur-sm"
        onClick={onClose}
        data-testid="entity-modal-backdrop"
      />
      <div className="relative w-full max-w-2xl bg-[#F8F8F4] border border-[#E2E4E0] rounded-lg shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-[#E2E4E0] bg-white">
          {canGoBack && <BackButton onClick={onBack} />}
          <Icon size={18} className="text-[#0A1128]" />
          <h2 className="text-sm font-semibold text-[#0A1128] flex-1 truncate" data-testid="entity-modal-title">
            {data?.full_name || data?.classroom_name || meta.label}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1 hover:bg-[#F5F5F0] rounded"
            data-testid="entity-modal-close"
            aria-label="إغلاق"
          >
            <X size={16} className="text-[#5C6B8C]" />
          </button>
        </div>

        {/* Body */}
        <div className="max-h-[75vh] overflow-y-auto p-4">
          {loading && (
            <div className="flex items-center justify-center py-12 text-[#5C6B8C] text-sm gap-2" data-testid="entity-modal-loading">
              <Loader2 size={16} className="animate-spin" />
              جاري تحميل التفاصيل...
            </div>
          )}
          {!loading && error && (
            <div className="text-center text-sm text-rose-700 py-10" data-testid="entity-modal-error">
              {error}
            </div>
          )}
          {!loading && !error && data && (
            <Body data={data} onOpen={onOpenEntity} onNavigate={onNavigate} />
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default EntityDetailModal;
